import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { DataSet, PrimaryDataSet, createColumnName, ColumnId } from '../../types';
import { MeasurementScale, HomogeneousArray } from '../../types/scale.types';
import { ROW_ID_COLUMN_ID } from '../../types/system-columns';
import { getColumnIdByName, getColumnData } from '../utils/column-utils';
import { formatDataValue } from '../utils/formatters';
import { ScaleEditModal } from './ScaleEditModal';
import { RowContextMenu } from './RowContextMenu';
import { CellContextMenu } from './CellContextMenu';
import { CommentModal } from './CommentModal';
import { ExcludeRowModal } from './ExcludeRowModal';
import { DataTableMenu } from './DataTableMenu';
import { DataTableReportModal } from './DataTableReportModal';
import { ExportDataModal, ExportDataOptions } from './ExportDataModal';
import { ExportFormat } from '../utils/dataExportUtils';
import { useRowExclusion } from '../hooks/useRowExclusion';
import { useRowComments } from '../hooks/useRowComments';
import { useDataTableReport } from '../hooks/useDataTableReport';
import { useTooltip } from '../hooks/useTooltip';
import { useProjectData } from '../stores/hooks/useProjectData';
import { useNotificationStore } from '../stores/notificationStore';
import logger from '../utils/logger';
import * as styles from '../styles/components/data-table.css.ts';

/**
 * SelectableDataTable - Interactive data table with selection capabilities
 * 
 * Features:
 * - Tabular data display with virtual scrolling
 * - Column/row selection functionality (single/multiple selection with Ctrl/Cmd key support)
 * - Sort functionality (ascending/descending)
 * - Virtual scroll rendering (displays only visible rows for performance)
 * - Data type-specific icon display
 * - Column resizing and renaming
 * 
 * This component provides full interactive functionality including row and column selection.
 * For display-only data tables, use DataTableView instead.
 */
interface SelectableDataTableProps {
  dataset: DataSet;
  onColumnSelect: (columnNames: string[]) => void;  // 必須：列選択時のコールバック
  onRowSelect: (rowIndices: number[]) => void;     // 必須：行選択時のコールバック
  selectedColumns: string[];                        // 必須：選択中の列
  selectedRows: number[];                           // 必須：選択中の行
  filteredRowIndices?: number[] | null;             // Filtered row indices (null means show all data)
  onSortChange: (sortConditions: Array<{ column: string; direction: 'asc' | 'desc' }>) => void; // 必須：ソート変更コールバック
  sortConditions: Array<{ column: string; direction: 'asc' | 'desc' }>; // 必須：現在のソート条件
  onColumnRename?: (columnId: string, newName: string) => void; // 列名変更コールバック
  onScaleChange?: (columnId: string, scale: MeasurementScale, ordinalOrder?: HomogeneousArray, validCategories?: HomogeneousArray) => void; // 尺度変更コールバック
  onOpenColumnTypeConversion?: (columnId: string) => void; // 列型変換タブを開くコールバック
  onViewSQLQuery?: () => void; // SQLクエリを表示するコールバック
  onReloadDataset?: () => void; // データセットリロードコールバック
  showSQLOption?: boolean; // SQLオプションを表示するかどうか
  showReloadOption?: boolean; // リロードオプションを表示するかどうか
}

export function SelectableDataTable({
  dataset,
  onColumnSelect,
  onRowSelect,
  selectedColumns,
  selectedRows,
  filteredRowIndices = null,
  onSortChange,
  sortConditions,
  onColumnRename,
  onScaleChange,
  onOpenColumnTypeConversion,
  onViewSQLQuery,
  onReloadDataset,
  showSQLOption = false,
  showReloadOption = false
}: SelectableDataTableProps) {
  // プロジェクトデータを取得（Materialized View用）
  const { updateDataset } = useProjectData();

  // 通知機能を取得
  const { success: showSuccessNotification } = useNotificationStore();

  // 行除外機能を使用
  const {
    handleExcludeRows,
    excludedRowNumbers,
    isExclusionSupported
  } = useRowExclusion(dataset.type === 'primary' ? dataset as PrimaryDataSet : undefined);

  // 行コメント機能を使用
  const {
    handleAddComment: addComment,
    handleUpdateComment: updateComment,
    rowComments,
    isCommentsSupported
  } = useRowComments(dataset.type === 'primary' ? dataset as PrimaryDataSet : undefined);

  // レポート機能を使用
  const {
    showReportModal,
    reports,
    openReportModal,
    closeReportModal,
    addDataTableToReport
  } = useDataTableReport(dataset);

  // セルツールチップ（500msホバー後に表示）
  const cellTooltip = useTooltip({ offset: { x: 10, y: 10 }, delay: 500 });

  // エクスポートモーダルの状態
  const [showExportModal, setShowExportModal] = useState(false);

  // Materialized Viewの状態を計算
  const isMaterialized = useMemo(() => {
    if (dataset.type !== 'derived') return false;
    return dataset.materialized === true;
  }, [dataset]);

  // Materialized Viewをトグル
  const handleToggleMaterialize = useCallback(() => {
    if (dataset.type !== 'derived') return;

    const newMaterialized = !dataset.materialized;

    updateDataset(dataset.id, (ds) => {
      if (ds.type !== 'derived') return ds;
      return {
        ...ds,
        materialized: newMaterialized
      };
    });

    logger.info('[SelectableDataTable] Toggled Materialized View:', {
      datasetId: dataset.id,
      datasetName: dataset.name,
      materialized: newMaterialized
    });
  }, [dataset, updateDataset]);
  

  const ROW_HEIGHT = 41; // 各行の固定高さ（padding + border込み）
  const [lastClickedRow, setLastClickedRow] = useState<number | null>(null);
  const [editingColumnId, setEditingColumnId] = useState<string | null>(null); // 編集中の列ID
  const [editingColumnName, setEditingColumnName] = useState<string>(''); // 編集中の列名
  const [editingScaleColumnId, setEditingScaleColumnId] = useState<string | null>(null); // 尺度編集中の列ID

  // Edit Mode: 全セルが編集可能な状態（PrimaryDataSetのみ）
  const [isEditMode, setIsEditMode] = useState(false);

  // セルの編集中の値を管理（Edit Mode時）
  // key: `${dataIndex}-${columnId}`
  const [editedValues, setEditedValues] = useState<Record<string, string>>({});
  
  const [contextMenu, setContextMenu] = useState<{
    visible: boolean;
    x: number;
    y: number;
    columnId: string;
    columnName: string;
  } | null>(null); // 列のコンテキストメニューの状態
  
  const [rowContextMenu, setRowContextMenu] = useState<{
    visible: boolean;
    x: number;
    y: number;
    rowIndex: number;
    rowNumber: number;
  } | null>(null); // 行のコンテキストメニューの状態

  const [cellContextMenu, setCellContextMenu] = useState<{
    visible: boolean;
    x: number;
    y: number;
    cellValue: unknown;
    columnName: string;
  } | null>(null); // セルのコンテキストメニューの状態

  const [commentModal, setCommentModal] = useState<{
    isOpen: boolean;
    rowIndices: number[];  // 複数行対応
    rowNumbers: number[];  // 複数のRowNumber
    rowData?: Array<{ rowNumber: number; values: Record<string, unknown> }>;  // 複数行のデータ
    initialComment?: string;
  } | null>(null);
  
  const [excludeModal, setExcludeModal] = useState<{
    isOpen: boolean;
    rowIndices: number[];
    rowData?: Array<{ rowNumber: number; values: Record<string, unknown> }>;
  } | null>(null); // 除外モーダルの状態
  
  // ツールチップの状態
  const [tooltip, setTooltip] = useState<{
    visible: boolean;
    x: number;
    y: number;
    content: string;
  }>({ visible: false, x: 0, y: 0, content: '' });
  
  // カラムリサイズ用の状態
  const [columnWidths, setColumnWidths] = useState<Record<string, number>>({});
  const [isResizing, setIsResizing] = useState<{
    columnId: string;
    startX: number;
    startWidth: number;
  } | null>(null);
  
  const tableWrapperRef = useRef<HTMLDivElement>(null);
  const virtualScrollerRef = useRef<HTMLDivElement>(null);
  
  // DerivedDataSetの処理をuseMemoで計算
  const derivedDataState = useMemo(() => {
    if (dataset.type === 'derived') {
      return {
        data: dataset.data || null,
        loading: false,
        error: null
      };
    }
    return {
      data: null,
      loading: false,
      error: null
    };
  }, [dataset]);

  // 表示用のカラム定義を取得（RowNumber列を除外）
  const displayColumns = useMemo(() => {
    return dataset.schema.columns.filter(column => column.id !== ROW_ID_COLUMN_ID);
  }, [dataset]);

  // 全列のデフォルト幅を初期化（ウィンドウ幅に基づいて計算）
  useEffect(() => {
    const newColumnWidths: Record<string, number> = {};
    const hasNewColumns = displayColumns.some(column => {
      const columnKey = column.id || column.name;
      return !(columnKey in columnWidths);
    });
    
    if (hasNewColumns && tableWrapperRef.current) {
      // テーブルコンテナの利用可能幅を取得
      const containerWidth = tableWrapperRef.current.clientWidth;
      const rowNumberColumnWidth = 60; // 行番号列の幅
      const availableWidth = containerWidth - rowNumberColumnWidth;
      
      // 最小幅と推奨幅を設定
      const minColumnWidth = 100;
      const maxColumnWidth = 300;
      
      // 列数で利用可能幅を分割
      const calculatedWidth = Math.max(
        minColumnWidth,
        Math.min(maxColumnWidth, availableWidth / displayColumns.length)
      );
      
      displayColumns.forEach(column => {
        const columnKey = column.id || column.name;
        if (!(columnKey in columnWidths)) {
          newColumnWidths[columnKey] = Math.floor(calculatedWidth);
        }
      });
      
      if (Object.keys(newColumnWidths).length > 0) {
        setColumnWidths(prev => ({ ...prev, ...newColumnWidths }));
      }
    }
  }, [displayColumns, columnWidths]);

  // 表示用のデータを取得
  const displayData = useMemo(() => {
    if (dataset.type === 'derived' && derivedDataState.data) {
      return derivedDataState.data;
    }
    if (dataset.type === 'primary') {
      return dataset.data;
    }
    return {};
  }, [dataset, derivedDataState.data]);

  // 表示用の行数を取得
  const displayRowCount = useMemo(() => {
    if (dataset.type === 'derived' && derivedDataState.data) {
      const firstColumnData = Object.values(derivedDataState.data)[0];
      return firstColumnData ? firstColumnData.length : 0;
    }
    return dataset.rowCount || 0;
  }, [dataset, derivedDataState.data]);

  // Calculate sorted data index array
  const sortedIndices = useMemo(() => {
    // フィルタが適用されている場合はフィルタされた行のみ、そうでなければ全行
    const baseIndices: number[] = filteredRowIndices !== null ? filteredRowIndices : Array.from({ length: displayRowCount }, (_, i) => i);
    
    if (sortConditions.length > 0) {
      baseIndices.sort((a, b) => {
        for (const condition of sortConditions) {
          // 列名から列IDを取得してデータにアクセス
          const columnId = getColumnIdByName(dataset, createColumnName(condition.column));
          if (!columnId) continue;

          const columnData = displayData[columnId];
          if (!columnData) continue;

          const valueA = columnData[a];
          const valueB = columnData[b];

          // null値の処理
          if (valueA === null && valueB === null) continue;
          if (valueA === null) return condition.direction === 'asc' ? 1 : -1;
          if (valueB === null) return condition.direction === 'asc' ? -1 : 1;

          // 型に応じた比較
          let comparison = 0;
          if (typeof valueA === 'number' && typeof valueB === 'number') {
            comparison = valueA - valueB;
          } else {
            comparison = String(valueA).localeCompare(String(valueB));
          }

          const result = condition.direction === 'asc' ? comparison : -comparison;
          if (result !== 0) return result;
        }
        return 0;
      });
    }
    
    return baseIndices;
  }, [dataset, sortConditions, filteredRowIndices, displayRowCount, displayData]);

  // 仮想スクロール用の状態
  const [scrollTop, setScrollTop] = useState(0);
  const [containerHeight, setContainerHeight] = useState(600); // キャッシュされたコンテナ高さ

  // データセット変更時のスクロールリセット用
  const prevDatasetId = useRef(dataset.id);
  useEffect(() => {
    if (prevDatasetId.current !== dataset.id) {
      prevDatasetId.current = dataset.id;
      setScrollTop(0);
      // 仮想スクローラーもリセット
      if (virtualScrollerRef.current) {
        virtualScrollerRef.current.scrollTop = 0;
      }
      if (tableWrapperRef.current) {
        tableWrapperRef.current.scrollTop = 0;
      }
    }
  }, [dataset.id]);

  // コンテナ高さをキャッシュし、リサイズ時のみ更新（強制リフロー回避）
  useEffect(() => {
    if (!tableWrapperRef.current) return;

    const updateContainerHeight = () => {
      if (tableWrapperRef.current) {
        setContainerHeight(tableWrapperRef.current.clientHeight || 600);
      }
    };

    // 初回測定
    updateContainerHeight();

    // ResizeObserverでリサイズを監視
    const resizeObserver = new ResizeObserver(() => {
      updateContainerHeight();
    });

    resizeObserver.observe(tableWrapperRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  // 仮想スクロール用の計算
  const totalRowCount = sortedIndices.length;
  // Edit Mode時は「+ Add Row」行の高さを追加
  const addRowHeight = isEditMode ? ROW_HEIGHT : 0;
  const totalContentHeight = totalRowCount * ROW_HEIGHT + addRowHeight;

  // 表示領域の計算（キャッシュされた値を使用）
  const visibleRowsInViewport = Math.ceil(containerHeight / ROW_HEIGHT);
  const bufferRows = Math.max(5, Math.ceil(visibleRowsInViewport * 0.2)); // バッファ行数
  
  // 現在のスクロール位置から表示開始行を計算
  const startRowIndex = Math.floor(scrollTop / ROW_HEIGHT);
  
  // 仮想スクロール：画面に表示される行のみレンダリング
  const visibleStartIndex = Math.max(0, startRowIndex - bufferRows);
  const visibleEndIndex = Math.min(
    startRowIndex + visibleRowsInViewport + bufferRows,
    totalRowCount
  );
  
  const visibleIndices = sortedIndices.slice(visibleStartIndex, visibleEndIndex);
  const offsetY = visibleStartIndex * ROW_HEIGHT;

  // テーブル全体の幅を計算（全列の幅の合計 + 行番号列の幅）
  const totalTableWidth = useMemo(() => {
    const columnsWidth = displayColumns.reduce((total, column) => {
      const columnKey = column.id || column.name;
      return total + (columnWidths[columnKey] || 150);
    }, 0);
    return columnsWidth + 60; // 行番号列の幅(60px)を追加
  }, [displayColumns, columnWidths]);

  // 仮想スクロールイベントハンドラ
  const handleScroll = useCallback((event: React.UIEvent<HTMLDivElement>) => {
    const target = event.currentTarget;
    const { scrollTop: newScrollTop } = target;
    
    setScrollTop(newScrollTop);
  }, []);

  // ウィンドウリサイズ時の列幅再計算
  useEffect(() => {
    const handleResize = () => {
      if (tableWrapperRef.current && displayColumns.length > 0) {
        // 既存の列幅がすべてデフォルト値（150px）の場合のみ再計算
        const allDefaultWidth = displayColumns.every(column => {
          const columnKey = column.id || column.name;
          return columnWidths[columnKey] === 150 || !columnWidths[columnKey];
        });
        
        if (allDefaultWidth) {
          const containerWidth = tableWrapperRef.current.clientWidth;
          const rowNumberColumnWidth = 60;
          const availableWidth = containerWidth - rowNumberColumnWidth;
          const minColumnWidth = 100;
          const maxColumnWidth = 300;
          
          const calculatedWidth = Math.max(
            minColumnWidth,
            Math.min(maxColumnWidth, availableWidth / displayColumns.length)
          );
          
          const newColumnWidths: Record<string, number> = {};
          displayColumns.forEach(column => {
            const columnKey = column.id || column.name;
            newColumnWidths[columnKey] = Math.floor(calculatedWidth);
          });
          
          setColumnWidths(newColumnWidths);
        }
      }
    };

    // 初期計算用の遅延実行
    const timeoutId = setTimeout(handleResize, 100);
    
    window.addEventListener('resize', handleResize);
    
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', handleResize);
    };
  }, [displayColumns, columnWidths]);

  // コンテキストメニューを閉じるためのクリックイベントリスナー
  useEffect(() => {
    const handleClickOutside = () => {
      setContextMenu(null);
      setRowContextMenu(null);
      setCellContextMenu(null);
    };

    if (contextMenu?.visible || rowContextMenu?.visible || cellContextMenu?.visible) {
      document.addEventListener('click', handleClickOutside);
      return () => {
        document.removeEventListener('click', handleClickOutside);
      };
    }
  }, [contextMenu?.visible, rowContextMenu?.visible, cellContextMenu?.visible]);

  const handleColumnClick = (columnName: string, event: React.MouseEvent) => {

    if (event.ctrlKey || event.metaKey) {
      // 複数選択
      const newSelection = selectedColumns.includes(columnName)
        ? selectedColumns.filter(col => col !== columnName)
        : [...selectedColumns, columnName];
      onColumnSelect(newSelection);
    } else {
      // 単一選択
      const newSelection = selectedColumns.includes(columnName) && selectedColumns.length === 1
        ? []
        : [columnName];
      onColumnSelect(newSelection);
    }
  };

  const handleColumnRightClick = (columnId: string, columnName: string, event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    
    setContextMenu({
      visible: true,
      x: event.clientX,
      y: event.clientY,
      columnId,
      columnName
    });
  };

  const handleContextMenuEdit = () => {
    if (contextMenu) {
      setEditingColumnId(contextMenu.columnId);
      setEditingColumnName(contextMenu.columnName);
      setContextMenu(null);
    }
  };

  const handleColumnNameEdit = (columnId: string) => {
    const column = displayColumns.find(col => col.id === columnId);
    if (column) {
      setEditingColumnId(columnId);
      setEditingColumnName(column.name);
    }
  };

  const handleColumnNameSave = () => {
    if (editingColumnId && editingColumnName.trim() && onColumnRename) {
      onColumnRename(editingColumnId, editingColumnName.trim());
      setEditingColumnId(null);
      setEditingColumnName('');
    }
  };

  const handleColumnNameCancel = () => {
    setEditingColumnId(null);
    setEditingColumnName('');
  };

  const handleColumnNameKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      handleColumnNameSave();
    } else if (event.key === 'Escape') {
      handleColumnNameCancel();
    }
  };

  const handleEditScale = () => {
    if (contextMenu) {
      setEditingScaleColumnId(contextMenu.columnId);
      setContextMenu(null);
    }
  };

  const handleScaleChange = (columnId: string, scale: MeasurementScale, ordinalOrder?: HomogeneousArray, validCategories?: HomogeneousArray) => {
    if (onScaleChange) {
      onScaleChange(columnId, scale, ordinalOrder, validCategories);
      setEditingScaleColumnId(null);
    }
  };

  const handleColumnSort = (columnName: string, event: React.MouseEvent) => {
    let newSortConditions: Array<{ column: string; direction: 'asc' | 'desc' }>;
    
    if (event.ctrlKey || event.metaKey) {
      // Ctrl/Cmd+クリック: 複数ソート（追加/変更）
      const existingIndex = sortConditions.findIndex(condition => condition.column === columnName);
      
      if (existingIndex >= 0) {
        // 既存の条件を変更
        newSortConditions = [...sortConditions];
        const currentDirection = newSortConditions[existingIndex].direction;
        
        if (currentDirection === 'asc') {
          newSortConditions[existingIndex].direction = 'desc';
        } else {
          // 降順から削除
          newSortConditions.splice(existingIndex, 1);
        }
      } else {
        // 新しい条件を追加
        newSortConditions = [...sortConditions, { column: columnName, direction: 'asc' }];
      }
    } else {
      // 通常クリック: 単一ソート
      const existingCondition = sortConditions.find(condition => condition.column === columnName);
      
      if (existingCondition) {
        if (existingCondition.direction === 'asc') {
          newSortConditions = [{ column: columnName, direction: 'desc' }];
        } else {
          newSortConditions = [];
        }
      } else {
        newSortConditions = [{ column: columnName, direction: 'asc' }];
      }
    }
    
    // ソート変更を親コンポーネントに通知
    onSortChange(newSortConditions);
    
    // ソート時にlastClickedRowをリセット（Shift+クリック選択の基準点をクリア）
    setLastClickedRow(null);
  };

  const handleRowRightClick = (rowIndex: number, event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();

    // RowNumber列から実際のRowNumber値を取得
    const rowNumberData = dataset.type === 'primary'
      ? getColumnData(dataset as PrimaryDataSet, ROW_ID_COLUMN_ID)
      : null;

    if (!rowNumberData) return;

    const rowNumber = rowNumberData[rowIndex] as number;

    setRowContextMenu({
      visible: true,
      x: event.clientX,
      y: event.clientY,
      rowIndex,
      rowNumber
    });
  };

  // セルの右クリックハンドラー
  const handleCellRightClick = (cellValue: unknown, columnName: string, event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();

    // 行コンテキストメニューを閉じる
    setRowContextMenu(null);
    setContextMenu(null);

    setCellContextMenu({
      visible: true,
      x: event.clientX,
      y: event.clientY,
      cellValue,
      columnName
    });
  };

  // セル値をクリップボードにコピー
  const handleCopyCellValue = async () => {
    if (!cellContextMenu) return;

    const { cellValue } = cellContextMenu;
    // unknownをDataValueにキャスト（cellValueはformatDataValueで処理できる型のはず）
    const formattedValue = formatDataValue(cellValue as string | number | boolean | null, typeof cellValue === 'number' ? 'float64' : 'string');

    try {
      await navigator.clipboard.writeText(String(formattedValue));
      showSuccessNotification('Copied!', { duration: 2000 });
      logger.info('[SelectableDataTable] Cell value copied to clipboard:', { value: formattedValue });
    } catch (error) {
      logger.error('[SelectableDataTable] Failed to copy cell value:', error);
    }
  };

  // セル編集可能かどうか（PrimaryDataSetのみ）
  const isCellEditable = dataset.type === 'primary';

  // Edit Modeの開始
  const handleStartEditMode = useCallback(() => {
    if (!isCellEditable) return;
    setIsEditMode(true);
    setEditedValues({});
    // Edit Mode中は行/列選択をクリア
    onColumnSelect([]);
    onRowSelect([]);
    logger.info('[SelectableDataTable] Edit Mode started');
  }, [isCellEditable, onColumnSelect, onRowSelect]);

  // Edit Modeの終了（変更を保存）
  const handleEndEditMode = useCallback(() => {
    if (!isEditMode || dataset.type !== 'primary') {
      setIsEditMode(false);
      setEditedValues({});
      return;
    }

    // 変更があるセルをまとめて保存
    const changes = Object.entries(editedValues);
    if (changes.length === 0) {
      setIsEditMode(false);
      setEditedValues({});
      return;
    }

    updateDataset(dataset.id, (ds) => {
      if (ds.type !== 'primary') return ds;

      const newData = { ...ds.data };

      for (const [key, newValue] of changes) {
        // keyは `${dataIndex}-${columnId}` 形式。columnIdに'-'が含まれる可能性があるため、
        // 最初の'-'の位置でのみ分割する
        const firstDashIndex = key.indexOf('-');
        const dataIndexStr = key.substring(0, firstDashIndex);
        const columnId = key.substring(firstDashIndex + 1);
        const dataIndex = parseInt(dataIndexStr, 10);
        const actualRowIndex = sortedIndices[dataIndex];
        const typedColumnId = columnId as ColumnId;

        const column = displayColumns.find(c => c.id === columnId);
        if (!column) continue;

        const columnData = newData[typedColumnId];
        if (!columnData) continue;

        // 元の値を取得
        const originalValue = columnData[actualRowIndex];
        const formattedOriginal = formatDataValue(
          originalValue as string | number | boolean | null,
          column.type
        );

        // 変更がなければスキップ
        if (newValue === formattedOriginal) continue;

        // 値を適切な型に変換
        let convertedValue: string | number | boolean | null;
        if (newValue === '' || newValue === 'null' || newValue === '(null)') {
          convertedValue = null;
        } else if (column.type === 'int64') {
          const parsed = parseInt(newValue, 10);
          convertedValue = isNaN(parsed) ? null : parsed;
        } else if (column.type === 'float64') {
          const parsed = parseFloat(newValue);
          convertedValue = isNaN(parsed) ? null : parsed;
        } else if (column.type === 'boolean') {
          convertedValue = newValue.toLowerCase() === 'true';
        } else {
          convertedValue = newValue;
        }

        // 配列をコピーして更新
        const newColumnData = [...columnData];
        newColumnData[actualRowIndex] = convertedValue;
        newData[typedColumnId] = newColumnData;

        logger.info('[SelectableDataTable] Cell value updated:', {
          datasetId: ds.id,
          columnId: typedColumnId,
          rowIndex: actualRowIndex,
          oldValue: originalValue,
          newValue: convertedValue
        });
      }

      return {
        ...ds,
        data: newData
      };
    });

    setIsEditMode(false);
    setEditedValues({});
    logger.info('[SelectableDataTable] Edit Mode ended, changes saved');
  }, [isEditMode, dataset, editedValues, sortedIndices, displayColumns, updateDataset]);

  // Edit Modeのキャンセル（変更を破棄）
  const handleCancelEditMode = useCallback(() => {
    setIsEditMode(false);
    setEditedValues({});
    logger.info('[SelectableDataTable] Edit Mode cancelled');
  }, []);

  // Edit Mode時のセル値変更
  const handleEditModeValueChange = useCallback((dataIndex: number, columnId: string, value: string) => {
    const key = `${dataIndex}-${columnId}`;
    setEditedValues(prev => ({
      ...prev,
      [key]: value
    }));
  }, []);

  // Edit Mode時の行追加
  const handleAddRow = useCallback(() => {
    if (!isEditMode || dataset.type !== 'primary') return;

    updateDataset(dataset.id, (ds) => {
      if (ds.type !== 'primary') return ds;

      const newData = { ...ds.data };
      const newRowCount = ds.rowCount + 1;

      // 各列に新しい値を追加
      for (const column of ds.schema.columns) {
        const typedColumnId = column.id as ColumnId;
        const columnData = newData[typedColumnId];
        if (!columnData) continue;

        // RowNumber列は最大値+1を設定
        if (column.id === ROW_ID_COLUMN_ID) {
          const maxRowNumber = Math.max(...(columnData as number[]));
          newData[typedColumnId] = [...columnData, maxRowNumber + 1];
        } else {
          // その他の列はnullを追加
          newData[typedColumnId] = [...columnData, null];
        }
      }

      logger.info('[SelectableDataTable] Row added', { datasetId: ds.id, newRowCount });

      return {
        ...ds,
        data: newData,
        rowCount: newRowCount
      };
    });
  }, [isEditMode, dataset, updateDataset]);

  // Edit Mode時の行削除
  const handleDeleteRow = useCallback((rowIndex: number) => {
    if (!isEditMode || dataset.type !== 'primary') return;

    updateDataset(dataset.id, (ds) => {
      if (ds.type !== 'primary') return ds;

      const newData = { ...ds.data };
      const newRowCount = ds.rowCount - 1;

      // 各列からその行のデータを削除
      for (const column of ds.schema.columns) {
        const typedColumnId = column.id as ColumnId;
        const columnData = newData[typedColumnId];
        if (!columnData) continue;

        // 配列から該当行を削除
        newData[typedColumnId] = [
          ...columnData.slice(0, rowIndex),
          ...columnData.slice(rowIndex + 1)
        ];
      }

      logger.info('[SelectableDataTable] Row deleted', { datasetId: ds.id, rowIndex, newRowCount });

      return {
        ...ds,
        data: newData,
        rowCount: newRowCount
      };
    });

    // コンテキストメニューを閉じる
    setRowContextMenu(null);
  }, [isEditMode, dataset, updateDataset]);

  const handleExclude = () => {
    if (!rowContextMenu || !isExclusionSupported) return;
    
    // 現在選択されている行を除外対象として設定
    const rowsToExclude = selectedRows.includes(rowContextMenu.rowIndex)
      ? selectedRows
      : [rowContextMenu.rowIndex];
    
    // 行データを収集（全列分）
    const rowData = rowsToExclude.map(rowIndex => {
      const rowValues: Record<string, unknown> = {};
      // 全ての表示列のデータを取得（RowNumber列は除外）
      const columnsToShow = displayColumns.filter(column => column.id !== ROW_ID_COLUMN_ID);
      
      columnsToShow.forEach(column => {
        const columnData = dataset.type === 'primary' 
          ? getColumnData(dataset, column.id)
          : displayData[column.id];
        if (columnData) {
          rowValues[column.name] = columnData[rowIndex];
        }
      });
      
      // RowNumber列から実際の行番号を取得
      const rowIdColumn = dataset.schema.columns.find(col => col.id === ROW_ID_COLUMN_ID);
      let rowNumber = rowIndex;
      if (rowIdColumn && dataset.data) {
        const columnData = getColumnData(dataset as PrimaryDataSet, rowIdColumn.id);
        if (columnData) {
          rowNumber = columnData[rowIndex] as number;
        }
      }
      
      return { rowNumber, values: rowValues };
    });
    
    // 除外モーダルを表示
    setExcludeModal({
      isOpen: true,
      rowIndices: rowsToExclude,
      rowData
    });
    setRowContextMenu(null);
  };
  
  // 除外を確定
  const handleConfirmExclude = (comment: string) => {
    if (!excludeModal) return;
    
    handleExcludeRows(excludeModal.rowIndices, comment);
    setExcludeModal(null);
    // 選択をクリア
    onRowSelect([]);
  };
  
  const handleRestore = () => {
    // 復元はExcludedRowsTabからのみ実行される
    logger.warn('[SelectableDataTable] Restore should be performed from ExcludedRowsTab');
    setRowContextMenu(null);
  };
  
  const handleOpenCommentModal = () => {
    if (!rowContextMenu || !isCommentsSupported) return;
    
    // 現在選択されている行を対象とする
    const targetIndices = selectedRows.includes(rowContextMenu.rowIndex)
      ? selectedRows
      : [rowContextMenu.rowIndex];
    
    // 各行のRowNumberを取得
    const rowIdColumn = dataset.schema.columns.find(col => col.id === ROW_ID_COLUMN_ID);
    const targetRowNumbers = targetIndices.map(idx => {
      if (rowIdColumn && dataset.data) {
        const columnData = getColumnData(dataset as PrimaryDataSet, rowIdColumn.id);
        if (columnData) {
          return columnData[idx] as number;
        }
      }
      return idx;
    });
    
    // 複数行のデータを収集
    const rowData = targetIndices.map((rowIndex, i) => {
      const rowValues: Record<string, unknown> = {};
      const columnsToShow = displayColumns.filter(column => column.id !== ROW_ID_COLUMN_ID);
      columnsToShow.forEach(column => {
        const columnData = dataset.type === 'primary' 
          ? getColumnData(dataset, column.id)
          : displayData[column.id];
        if (columnData) {
          rowValues[column.name] = columnData[rowIndex];
        }
      });
      return { rowNumber: targetRowNumbers[i], values: rowValues };
    });
    
    // 最初の行の既存コメントを取得（複数行選択時は空）
    const existingComment = targetIndices.length === 1
      ? rowComments[targetRowNumbers[0]]?.comment
      : undefined;
    
    setCommentModal({
      isOpen: true,
      rowIndices: targetIndices,
      rowNumbers: targetRowNumbers,
      rowData,
      initialComment: existingComment
    });
    setRowContextMenu(null);
  };
  
  const handleSaveComment = (comment: string) => {
    if (!commentModal || !isCommentsSupported) return;
    
    // 複数行に同じコメントを適用
    commentModal.rowNumbers.forEach(rowNumber => {
      const existingComment = rowComments[rowNumber];
      if (existingComment) {
        updateComment(rowNumber, comment);
      } else {
        addComment(rowNumber, comment);
      }
    });
    
    setCommentModal(null);
    
    // 複数行選択の場合は選択を解除
    if (commentModal.rowIndices.length > 1) {
      onRowSelect([]);
    }
  };

  const handleRowClick = (rowIndex: number, event: React.MouseEvent) => {
    if (event.shiftKey && lastClickedRow !== null) {
      // Shift+クリック: 範囲選択
      // sortedIndices配列内での位置を取得
      const currentRowPositionInSorted = sortedIndices.indexOf(rowIndex);
      const lastClickedRowPositionInSorted = sortedIndices.indexOf(lastClickedRow);
      
      if (currentRowPositionInSorted !== -1 && lastClickedRowPositionInSorted !== -1) {
        const startPosition = Math.min(lastClickedRowPositionInSorted, currentRowPositionInSorted);
        const endPosition = Math.max(lastClickedRowPositionInSorted, currentRowPositionInSorted);
        
        // ソート済み配列から実際の行インデックスを取得
        const rangeSelection = sortedIndices.slice(startPosition, endPosition + 1);
        
        // 既存の選択と結合（重複を除去）
        const newSelection = [...new Set([...selectedRows, ...rangeSelection])].sort((a, b) => a - b);
        onRowSelect(newSelection);
      }
      // 範囲選択時はlastClickedRowを更新しない（次回のShift+クリックで同じ起点を使用）
    } else if (event.ctrlKey || event.metaKey) {
      // Ctrl/Cmd+クリック: 複数選択
      const newSelection = selectedRows.includes(rowIndex)
        ? selectedRows.filter(idx => idx !== rowIndex)
        : [...selectedRows, rowIndex];
      onRowSelect(newSelection);
      setLastClickedRow(rowIndex);
    } else {
      // 通常クリック: 単一選択
      const newSelection = selectedRows.includes(rowIndex) && selectedRows.length === 1
        ? []
        : [rowIndex];
      onRowSelect(newSelection);
      setLastClickedRow(rowIndex);
    }
  };

  // カラムリサイズハンドラー
  const handleResizeStart = (columnId: string, event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    
    const currentWidth = columnWidths[columnId] || 150; // フォールバック幅
    setIsResizing({
      columnId,
      startX: event.clientX,
      startWidth: currentWidth
    });
    
    // リサイズ中はbody要素にクラスを追加
    document.body.classList.add('resizing-column');
  };

  // グローバルマウスイベントでリサイズ処理
  useEffect(() => {
    const handleMouseMove = (event: MouseEvent) => {
      if (!isResizing) return;
      
      // preventDefault でブラウザのデフォルト動作（選択など）を防止
      event.preventDefault();
      
      const deltaX = event.clientX - isResizing.startX;
      const newWidth = Math.max(50, isResizing.startWidth + deltaX); // 最小幅50px
      
      setColumnWidths(prev => ({
        ...prev,
        [isResizing.columnId]: newWidth
      }));
    };

    const handleMouseUp = (event: MouseEvent) => {
      event.preventDefault();
      setIsResizing(null);

      // リサイズ終了時にbody要素からクラスを削除
      document.body.classList.remove('resizing-column');
    };

    if (isResizing) {
      // passive: false を指定してpreventDefaultを有効にする
      document.addEventListener('mousemove', handleMouseMove, { passive: false });
      document.addEventListener('mouseup', handleMouseUp, { passive: false });
      
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        // クリーンアップ時にもbody要素からクラスを削除
        document.body.classList.remove('resizing-column');
      };
    }
  }, [isResizing]);



  // エクスポート処理
  const handleExport = useCallback(async (
    filePath: string,
    format: ExportFormat,
    options: ExportDataOptions
  ) => {
    try {
      logger.info('[SelectableDataTable] Starting export', {
        filePath,
        format,
        options,
        datasetName: dataset.name
      });

      // Web版の場合
      const { webExportService } = await import('../services/webExportService');
      await webExportService.exportDataset(
        dataset,
        format,
        {
          includeHeaders: options.includeHeaders,
          selectedRowsOnly: options.selectedRowsOnly,
          selectedRows,
          filteredRowIndices,
          sortedRowIndices: sortedIndices
        }
      );
      logger.info('[SelectableDataTable] Export completed successfully (Web)');
    } catch (error) {
      logger.error('[SelectableDataTable] Export error', error as Error);
      throw error;
    }
  }, [dataset, selectedRows, filteredRowIndices, sortedIndices]);

  return (
    <div className={`${styles.dataTableContainer} ${styles.selectable}`} data-testid="data-table-container">
      {/* データテーブルメニュー（右上の縦三点リーダ） */}
      {!isEditMode && (
        <DataTableMenu
          onAddToReport={openReportModal}
          onExport={() => setShowExportModal(true)}
          onViewSQLQuery={onViewSQLQuery}
          onReloadDataset={onReloadDataset}
          showSQLOption={showSQLOption}
          showReloadOption={showReloadOption}
          isMaterialized={isMaterialized}
          onToggleMaterialize={handleToggleMaterialize}
          showMaterializeOption={dataset.type === 'derived'}
          onStartEditMode={handleStartEditMode}
          showEditOption={isCellEditable}
        />
      )}

      {/* Edit Mode用ツールバー */}
      {isEditMode && (
        <div className={styles.editModeToolbar} data-testid="edit-mode-toolbar">
          <span className={styles.editModeLabel}>Edit Mode</span>
          <div style={{ flex: 1 }} />
          <button
            className={styles.editModeCancelButton}
            onClick={handleCancelEditMode}
            data-testid="edit-mode-cancel"
          >
            Cancel
          </button>
          <button
            className={styles.editModeSaveButton}
            onClick={handleEndEditMode}
            data-testid="edit-mode-done"
          >
            Done
          </button>
        </div>
      )}

      {/* DerivedDataSetのローディング・エラー表示 */}
      {dataset.type === 'derived' && derivedDataState.loading && (
        <div className={styles.loadingMessage}>
          Evaluating derived dataset...
        </div>
      )}
      {dataset.type === 'derived' && derivedDataState.error && (
        <div className={styles.errorMessage}>
          Error: {derivedDataState.error}
        </div>
      )}
      
      <div
        className={styles.tableWrapper}
        data-testid="table-wrapper"
        ref={tableWrapperRef}
        onScroll={handleScroll}
      >
        {/* 仮想スクロール用の全体高さを持つ要素 */}
        <div
          ref={virtualScrollerRef}
          style={{
            height: totalContentHeight, // 全行数に基づく高さ
            position: 'relative',
            pointerEvents: 'none'
          }}
        >
          {/* 実際のテーブル */}
          <div style={{ position: 'absolute', top: offsetY, width: '100%', pointerEvents: 'auto' }}>
            <table
              className={styles.dataTable}
              data-testid="data-table"
              style={{ width: totalTableWidth }}
            >
              <thead>
                <tr>
                  <th
                    className={`${styles.rowNumberHeader} ${styles.stickyHeader}`}
                    style={{ width: 60, minWidth: 60, maxWidth: 60 }}
                  >
                    #
                  </th>
                  {displayColumns.map((column) => {
                    const isSelected = selectedColumns.includes(column.name);
                    const isNumericType = column.type === 'int64' || column.type === 'float64';
                    const headerClasses = `${styles.columnHeader} ${styles.stickyHeader} ${isNumericType ? styles.numeric : ''} ${isSelected ? styles.selected : ''}`;
                    const isEditing = editingColumnId === column.id;
                    return (
                      <th
                        key={column.id || column.name}
                        className={headerClasses}
                        onClick={(e) => !isEditing && !isEditMode && handleColumnClick(column.name, e)}
                        onContextMenu={(e) => !isEditing && !isEditMode && handleColumnRightClick(column.id || column.name, column.name, e)}
                        data-testid={`column-header-${column.name}`}
                        style={{
                          width: columnWidths[column.id || column.name] || 150,
                          minWidth: columnWidths[column.id || column.name] || 150,
                          maxWidth: columnWidths[column.id || column.name] || 150
                        }}
                      >
                        <div className={styles.columnHeaderContent}>
                          {/* 1行目: 列名とソートボタン */}
                          <div className={styles.columnHeaderMainRow}>
                            <span className={styles.columnName}>
                              {isEditing ? (
                                <input
                                  type="text"
                                  value={editingColumnName}
                                  onChange={(e) => setEditingColumnName(e.target.value)}
                                  onKeyDown={handleColumnNameKeyDown}
                                  onBlur={handleColumnNameSave}
                                  className={styles.columnNameInput}
                                  autoFocus
                                  data-testid={`column-name-input-${column.id || column.name}`}
                                />
                              ) : (
                                <span
                                  onDoubleClick={() => handleColumnNameEdit(column.id || column.name)}
                                  className={styles.editableColumnName}
                                  title="Double-click to edit column name"
                                >
                                  {column.name}
                                </span>
                              )}
                            </span>
                            <button
                              className={styles.sortButton}
                              data-testid={`sort-button-${column.name}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleColumnSort(column.name, e);
                              }}
                              title={`${column.name}でソート (Ctrl/Cmd+クリックで複数ソート)`}
                            >
                              {(() => {
                                const condition = sortConditions.find(c => c.column === column.name);
                                if (condition) {
                                  const priority = sortConditions.length > 1 ? sortConditions.indexOf(condition) + 1 : '';
                                  const arrow = condition.direction === 'asc' ? '▲' : '▼';
                                  return `${arrow}${priority}`;
                                }
                                return '⬍';
                              })()}
                            </button>
                          </div>
                          {/* 2行目: データ型と測定尺度 */}
                          <div className={styles.columnHeaderMetaRow}>
                            <span className={styles.columnType}>{column.type}</span>
                            {column.scale && (
                              <span
                                className={styles.columnScale}
                                title={`Scale: ${column.scale.scale}${column.scale.inferredAutomatically ? ' (inferred)' : ' (user-defined)'}`}
                              >
                                {column.scale.scale}
                              </span>
                            )}
                          </div>
                          <div
                            className={styles.columnResizer}
                            onMouseDown={(e) => handleResizeStart(column.id || column.name, e)}
                            onClick={(e) => {
                              // リサイザーのクリックイベントが親要素に伝播しないように
                              e.stopPropagation();
                              e.preventDefault();
                            }}
                            data-testid={`column-resizer-${column.name}`}
                          />
                        </div>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {visibleIndices.map((dataIndex) => (
                  <tr
                    key={dataIndex}
                    className={`${styles.dataRow} ${!isEditMode && selectedRows.includes(dataIndex) ? styles.selected : ''}`}
                    onClick={(e) => !isEditMode && handleRowClick(dataIndex, e)}
                    onContextMenu={(e) => !isEditMode && handleRowRightClick(dataIndex, e)}
                  >
                    <td
                      className={styles.rowNumber}
                      style={{ width: 60, minWidth: 60, maxWidth: 60 }}
                      onContextMenu={(e) => isEditMode && handleRowRightClick(dataIndex, e)}
                    >
                      {(() => {
                        // RowNumberを取得
                        const rowIdColumn = dataset.schema.columns.find(col => col.id === ROW_ID_COLUMN_ID);
                        if (!rowIdColumn || !dataset.data) return dataIndex + 1; // フォールバック
                        const columnData = getColumnData(dataset as PrimaryDataSet, rowIdColumn.id);
                        if (!columnData) return dataIndex + 1; // フォールバック
                        const rowNumber = columnData[dataIndex] as number;
                        return rowNumber;
                      })()}
                      {/* コメントインジケーター */}
                      {(() => {
                        // RowNumberを取得
                        const rowIdColumn = dataset.schema.columns.find(col => col.id === ROW_ID_COLUMN_ID);
                        if (!rowIdColumn || !dataset.data) return null;
                        const columnData = getColumnData(dataset as PrimaryDataSet, rowIdColumn.id);
                        if (!columnData) return null;
                        const rowNumber = columnData[dataIndex] as number;
                        const comment = rowComments[rowNumber];
                        
                        if (comment) {
                          return (
                            <span 
                              className={styles.commentIndicator}
                              onMouseEnter={(e) => {
                                const rect = e.currentTarget.getBoundingClientRect();
                                setTooltip({
                                  visible: true,
                                  x: rect.right + 5,
                                  y: rect.top + rect.height / 2,
                                  content: comment.comment
                                });
                              }}
                              onMouseLeave={() => setTooltip({ visible: false, x: 0, y: 0, content: '' })}
                            >
                              💬
                            </span>
                          );
                        }
                        return null;
                      })()}
                    </td>
                    {displayColumns.map((column) => {
                      const columnData = dataset.type === 'primary'
                        ? getColumnData(dataset, column.id)
                        : displayData[column.id];
                      const cellValue = columnData ? columnData[dataIndex] : null;
                      const isNumericType = column.type === 'int64' || column.type === 'float64';
                      const formattedValue = formatDataValue(cellValue, column.type);
                      const editKey = `${dataIndex}-${column.id}`;
                      const editedValue = editedValues[editKey];
                      const displayValue = editedValue !== undefined ? editedValue : formattedValue;
                      return (
                        <td
                          key={`${dataIndex}-${column.id || column.name}`}
                          className={`${styles.dataCell} ${!isEditMode && selectedColumns.includes(column.name) ? styles.columnSelected : ''} ${isNumericType ? styles.numeric : ''} ${isEditMode ? styles.editing : ''}`}
                          style={{
                            width: columnWidths[column.id || column.name] || 150,
                            minWidth: columnWidths[column.id || column.name] || 150,
                            maxWidth: columnWidths[column.id || column.name] || 150
                          }}
                          onContextMenu={(e) => !isEditMode && handleCellRightClick(cellValue, column.name, e)}
                          onMouseEnter={(e) => !isEditMode && cellTooltip.show(formattedValue, e)}
                          onMouseLeave={cellTooltip.hide}
                        >
                          {isEditMode ? (
                            <input
                              type="text"
                              className={styles.cellInputEditMode}
                              value={displayValue}
                              onChange={(e) => handleEditModeValueChange(dataIndex, column.id, e.target.value)}
                              data-testid={`cell-input-${dataIndex}-${column.id}`}
                            />
                          ) : (
                            formattedValue
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
                {/* Edit Mode時の行追加用「+」行 */}
                {isEditMode && (
                  <tr
                    className={styles.addRowRow}
                    onClick={handleAddRow}
                    data-testid="add-row-button"
                  >
                    <td
                      colSpan={displayColumns.length + 1}
                      className={styles.addRowCell}
                    >
                      + Add Row
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>


      {/* コンテキストメニュー */}
      {contextMenu?.visible && (
        <div 
          className={styles.contextMenu}
          style={{
            left: contextMenu.x,
            top: contextMenu.y
          }}
          data-testid="column-context-menu"
        >
          <button
            className={styles.contextMenuItem}
            onClick={handleContextMenuEdit}
            data-testid="context-menu-edit-column"
          >
            Edit Column Name
          </button>
          <button
            className={styles.contextMenuItem}
            onClick={handleEditScale}
            data-testid="context-menu-edit-scale"
          >
            Edit Scale of Measurement
          </button>
          {onOpenColumnTypeConversion && (
            <button
              className={styles.contextMenuItem}
              onClick={() => {
                if (contextMenu.columnId) {
                  onOpenColumnTypeConversion(contextMenu.columnId);
                  setContextMenu({ ...contextMenu, visible: false });
                }
              }}
              data-testid="context-menu-convert-column-types"
            >
              Convert Column Types...
            </button>
          )}
        </div>
      )}

      {/* 尺度編集モーダル */}
      {editingScaleColumnId && (() => {
        const column = displayColumns.find(col => (col.id || col.name) === editingScaleColumnId);
        if (!column) return null;
        
        return (
          <ScaleEditModal
            column={column}
            onSave={(scale, ordinalOrder, validCategories) => 
              handleScaleChange(editingScaleColumnId, scale, ordinalOrder, validCategories)
            }
            onCancel={() => setEditingScaleColumnId(null)}
          />
        );
      })()}
      
      {/* 行のコンテキストメニュー */}
      {rowContextMenu && dataset.type === 'primary' && (
        <RowContextMenu
          visible={rowContextMenu.visible}
          x={rowContextMenu.x}
          y={rowContextMenu.y}
          rowIndex={rowContextMenu.rowIndex}
          isExcluded={excludedRowNumbers.includes(rowContextMenu.rowNumber)}
          hasComment={rowContextMenu.rowNumber in rowComments}
          isEditMode={isEditMode}
          onExclude={handleExclude}
          onRestore={handleRestore}
          onAddComment={handleOpenCommentModal}
          onEditComment={handleOpenCommentModal}
          onDeleteRow={() => handleDeleteRow(rowContextMenu.rowIndex)}
          onClose={() => setRowContextMenu(null)}
        />
      )}

      {/* セルのコンテキストメニュー */}
      {cellContextMenu && (
        <CellContextMenu
          visible={cellContextMenu.visible}
          x={cellContextMenu.x}
          y={cellContextMenu.y}
          cellValue={cellContextMenu.cellValue}
          onCopy={handleCopyCellValue}
          onClose={() => setCellContextMenu(null)}
        />
      )}

      {/* コメントモーダル */}
      {commentModal && (
        <CommentModal
          isOpen={commentModal.isOpen}
          initialComment={commentModal.initialComment}
          rowData={commentModal.rowData}
          onSave={handleSaveComment}
          onCancel={() => setCommentModal(null)}
        />
      )}
      
      {/* 除外モーダル */}
      {excludeModal && (
        <ExcludeRowModal
          isOpen={excludeModal.isOpen}
          rowCount={excludeModal.rowIndices.length}
          rowData={excludeModal.rowData}
          onConfirm={handleConfirmExclude}
          onCancel={() => setExcludeModal(null)}
        />
      )}
      
      {/* コメントツールチップ */}
      {tooltip.visible && (
        <div 
          className={styles.floatingTooltip}
          role="tooltip"
          style={{
            position: 'fixed',
            left: tooltip.x,
            top: tooltip.y,
            transform: 'translateY(-50%)',
            background: '#333',
            color: 'white',
            padding: '8px 12px',
            borderRadius: '4px',
            fontSize: '12px',
            zIndex: 10000,
            pointerEvents: 'none',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
            maxWidth: '300px',
            wordWrap: 'break-word',
            whiteSpace: 'pre-wrap'
          }}
        >
          {tooltip.content}
        </div>
      )}
      
      {/* データテーブルレポート追加モーダル - 開いている時のみレンダリング */}
      {showReportModal && (
        <DataTableReportModal
          reports={reports}
          dataset={dataset}
          onConfirm={addDataTableToReport}
          onClose={closeReportModal}
        />
      )}
      
      {/* データエクスポートモーダル */}
      {showExportModal && (
        <ExportDataModal
          isOpen={showExportModal}
          onClose={() => setShowExportModal(false)}
          onExport={handleExport}
          datasetName={dataset.name}
          hasSelectedRows={selectedRows.length > 0}
          selectedRowCount={selectedRows.length}
        />
      )}

      {/* セルツールチップ */}
      <cellTooltip.Tooltip />
    </div>
  );
}
